import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ActivityDashboardService } from '../alumni-activity-dashboard/activityDashboardService';
import {
    DashboardData, DashboardVersion, InstituteDescriptor, InstituteType, Period,
    VibrancyDashboardService,
} from './vibrancyDashboardService';

// ── Adapters: moengage_template campaign data → vibrancy dashboard shapes ───────
const VIB_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMoeDate = (d?: string) => {
    if (!d) return '';
    const [y, m, day] = String(d).split('-');
    const mi = Number(m) - 1;
    return VIB_MONTHS[mi] && day ? `${Number(day)} ${VIB_MONTHS[mi]} ${y}` : String(d);
};

const adaptEmailCampaign = (c: any) => ({
    name: c.name,
    meta: [fmtMoeDate(c.date), c.category].filter(Boolean).join(' · '),
    sent: c.sent ?? 0,
    openRate: c.openRate ?? 0,   // based on delivered (computed server-side)
    clicks: c.clicks ?? 0,
    external: false,
});

const adaptWhatsapp = (wa: any) => ({
    broadcasts: wa?.totalBroadcasts ?? 0,
    tiles: [
        { label: 'Delivered', value: String(wa?.delivered ?? 0) },
        { label: 'Read Rate', value: `${wa?.readRate ?? 0}%` },
        { label: 'Replied', value: String(wa?.replied ?? 0) },
        { label: 'Reply %', value: `${wa?.replyRate ?? 0}%` },
    ],
    comparePrev: '',
    compareDelta: '',
    perBroadcast: (wa?.broadcasts || []).slice(0, 3).map((b: any) => ({ name: b.name, readRate: b.readRate, color: '#198754' })),
});

interface VibrancyState {
    version: DashboardVersion;

    // Selection — ids are domain-data codes (SCHOOL_GROUP / INSTITUTE_DOMAIN), so string|number
    selectedGroupId: string | number | null;
    selectedGroupName: string;
    selectedInstituteId: string | number | null;
    selectedInstituteName: string;
    instituteType: InstituteType | null;
    instituteLoaded: boolean;

    // Client-version campus context
    campuses: { id: number; name: string }[];
    selectedCampusId: number | null;
    isGroupOverview: boolean;

    // Period + compare
    timeframe: Period;
    compareMode: boolean;
    comparePeriod: string;

    // Data
    data: DashboardData | null;
    loading: boolean;

    // SM highlight editor (shared open signal — header button + card button)
    highlightEditorOpen: boolean;
    setHighlightEditorOpen: (open: boolean) => void;

    // Setters
    setSelectedGroup: (id: string | number | null, name: string) => void;
    setSelectedInstitute: (id: string | number | null, name: string, type?: InstituteType | null) => void;
    setSelectedCampus: (id: number) => void;
    setTimeframe: (tf: Period) => void;
    setCompareMode: (on: boolean) => void;
    setComparePeriod: (p: string) => void;
    updateHighlight: (patch: Partial<NonNullable<DashboardData>['highlight']>) => void;
    refresh: () => void;
}

const VibrancyContext = createContext<VibrancyState>({} as VibrancyState);
export const useVibrancy = () => useContext(VibrancyContext);

interface ProviderProps {
    version: DashboardVersion;
    children: React.ReactNode;
}

export const VibrancyDashboardProvider: React.FC<ProviderProps> = ({ version, children }) => {
    const [selectedGroupId, setGroupId] = useState<string | number | null>(null);
    const [selectedGroupName, setGroupName] = useState('');
    const [selectedInstituteId, setInstituteId] = useState<string | number | null>(null);
    const [selectedInstituteName, setInstituteName] = useState('');
    const [instituteType, setInstituteType] = useState<InstituteType | null>(null);
    const [instituteLoaded, setInstituteLoaded] = useState(false);

    const [campuses, setCampuses] = useState<{ id: number; name: string }[]>([]);
    const [selectedCampusId, setSelectedCampusId] = useState<number | null>(null);

    const [timeframe, setTimeframe] = useState<Period>('monthly');
    const [compareMode, setCompareMode] = useState(false);
    const [comparePeriod, setComparePeriod] = useState('');

    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [highlightEditorOpen, setHighlightEditorOpen] = useState(false);

    const isGroupOverview = version === 'client' && selectedCampusId === 0;

    // Client institute selection is driven by the SelectorBar (session institutes,
    // defaulting to the primary institute) — no dummy auto-load here.

    const fetchData = useCallback(() => {
        if (!selectedInstituteId) { setData(null); return; }
        const descriptor: InstituteDescriptor = {
            id: selectedInstituteId,
            name: selectedInstituteName,
            type: instituteType ?? 'k12',
        };
        setLoading(true);
        // Base dashboard + Campaign Interactions (Email + WhatsApp) from moengage_template.
        // Best-effort: on failure we keep the base values so the section never breaks.
        Promise.all([
            VibrancyDashboardService.getDashboardData(descriptor, timeframe, version),
            ActivityDashboardService.getMoengageCampaigns(Number(selectedInstituteId), timeframe)
                .catch(() => null),
        ])
            .then(([r, moeRes]) => {
                const base: any = r.data.data;
                const moe: any = (moeRes as any)?.data?.data;
                if (base && moe) {
                    base.emailCampaigns = (moe.emailCampaigns || []).map(adaptEmailCampaign);
                    base.whatsapp = adaptWhatsapp(moe.whatsappStats);
                }
                setData(base);
            })
            .finally(() => setLoading(false));
    }, [selectedInstituteId, selectedInstituteName, instituteType, timeframe, version]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setSelectedGroup = (id: string | number | null, name: string) => {
        setGroupId(id);
        setGroupName(name);
        // Changing group clears institute selection (AC-S1.4)
        setInstituteId(null);
        setInstituteName('');
        setInstituteType(null);
        setInstituteLoaded(false);
    };

    const setSelectedInstitute = (id: string | number | null, name: string, type: InstituteType | null = null) => {
        setInstituteId(id);
        setInstituteName(name);
        setInstituteType(type);
        setInstituteLoaded(!!id);
        setTimeframe('monthly');       // default period on every fresh selection (AC-S1.3)
        setCompareMode(false);
    };

    const setSelectedCampus = (id: number) => setSelectedCampusId(id);

    const handleSetTimeframe = (tf: Period) => {
        setTimeframe(tf);
        setComparePeriod('');
        if (tf === 'monthly') setCompareMode(false); // VD-01 auto-deactivate compare
    };

    const updateHighlight = (patch: Partial<NonNullable<DashboardData>['highlight']>) => {
        setData(prev => prev ? { ...prev, highlight: { ...prev.highlight, ...patch } } : prev);
    };

    return (
        <VibrancyContext.Provider value={{
            version,
            selectedGroupId, selectedGroupName,
            selectedInstituteId, selectedInstituteName, instituteType, instituteLoaded,
            campuses, selectedCampusId, isGroupOverview,
            timeframe, compareMode, comparePeriod,
            data, loading,
            highlightEditorOpen, setHighlightEditorOpen,
            setSelectedGroup, setSelectedInstitute, setSelectedCampus,
            setTimeframe: handleSetTimeframe, setCompareMode, setComparePeriod,
            updateHighlight, refresh: fetchData,
        }}>
            {children}
        </VibrancyContext.Provider>
    );
};
